/**
 * The one copy of the browser contract. Mostly types; the few values here are
 * shared by the server and the web app, so this file must import nothing and
 * stay free of anything Node-only or browser-only.
 */

export type ToolKind = "carbonos" | "external";

export type UserRole = "admin" | "member";

export interface ManagedUser {
  id: string;
  name: string;
  email: string;
}

export interface ManageUsersResponse { users: ManagedUser[] }

export type Theme = "light" | "dark";

export interface Tool {
  /** React key, and the /api/tools/:key/logo path segment; never rendered. */
  key: string;
  name: string;
  /** Null unless the URL is http(s), checked on the server. */
  url: string | null;
  /** Any value other than "carbonos" from Gatekeeper becomes "external". */
  kind: ToolKind;
}

/** 200 body of GET /api/my-tools. */
export interface MyToolsResponse {
  /** Only the active admin role for this product grants user management. */
  role: UserRole;
  /** The real admin stays signed in while previewing this person. */
  viewingAs: ManagedUser | null;
  /** The carbon-os viewer's verified email, lower-cased. */
  signedInAs: string;
  /** Gatekeeper's preferred name; null means Gatekeeper does not know the person. */
  person: { name: string } | null;
  /** Gatekeeper's `apps`, sorted by name; [] means nothing has been set up. */
  tools: Tool[];
  /** ACCESS_REQUEST_URL, or null when unset. */
  accessRequestUrl: string | null;
  /** How the browser reports usage; null when POSTHOG_KEY is unset. */
  analytics: BrowserAnalytics | null;
  /** The theme saved in Gatekeeper; null when none is saved, or it cannot be read. */
  theme: Theme | null;
}

/**
 * 200 body of GET /api/session. CLERK_PUBLISHABLE_KEY, public by design: the
 * browser loads Clerk's script with it to keep the 60-second session cookie
 * fresh while the page is open. Null when sign-in is not configured.
 */
export interface SessionConfig {
  clerkPublishableKey: string | null;
}

/** PostHog settings for the browser. The key is the public project key. */
export interface BrowserAnalytics {
  /** This app's slug in the shared project, stamped on every event. */
  app: string;
  key: string;
  host: string;
  /** `carbon-os:<Clerk user id>`, the same id the server's events use. */
  distinctId: string;
  /** RAILWAY_ENVIRONMENT_NAME, or "local". */
  environment: string;
}

export type MyToolsErrorCode =
  /** 401: no carbon-os session, or Gatekeeper said 401. */
  | "not_signed_in"
  /** 403: the account's email is unverified. */
  | "wrong_account"
  /** 403: verified, but Gatekeeper does not know this person. Nobody gets in without it. */
  | "not_in_gatekeeper"
  /** 503: GATEKEEPER_URL unset, or Clerk unconfigured (development only). */
  | "not_connected"
  /** 502/503/504: timeout, 5xx, 503, network failure, bad body or unexpected status. */
  | "gatekeeper_unavailable";

export interface MyToolsError {
  /** A sentence for a person. */
  error: string;
  /** What the SPA branches on. */
  code: MyToolsErrorCode;
  /** "/auth/session" on 401. */
  signInUrl?: string;
  /** "/auth/signout" on 403. */
  signOutUrl?: string;
  /** On 403, so the page can say which account was refused. */
  signedInAs?: string;
  /** On 403 `not_in_gatekeeper`, where to raise a ticket; absent when unset. */
  accessRequestUrl?: string;
}

/** Body of PUT /api/theme. The person and the app come from the server, never from here. */
export interface SaveThemeRequest {
  theme: Theme;
}

export type SaveThemeErrorCode =
  /** 401: no carbon-os session. */
  | "not_signed_in"
  /** 400: the theme is not "light" or "dark". */
  | "bad_theme"
  /** 503: Clerk unconfigured (development only). */
  | "not_connected"
  /** 502/503: Clerk could not hand over the token, or Gatekeeper did not save it. */
  | "theme_not_saved";

/** Every PUT /api/theme answer other than its 204. */
export interface SaveThemeError {
  /** A sentence for a person. */
  error: string;
  code: SaveThemeErrorCode;
}

/** One training material, as the page renders it. */
export interface TrainingItem {
  /** Missing means a legacy material item. */
  itemType?: "material" | "process";
  /** Gatekeeper's material id, a UUID. The same material can sit in several tracks. */
  materialId: string;
  title: string;
  /** The tool it is about. */
  product: { name: string };
  kind: "file" | "video";
  /**
   * A video's own http(s) link, or null when it has none that is safe (an
   * uploaded video always has none: it plays from `/playback`). A file's
   * download on carbon-os, `/api/training/materials/<materialId>`: never Gatekeeper's.
   */
  url: string | null;
  /**
   * An UPLOADED video, as this person stands on it; null for a link video, a
   * file, or a Gatekeeper that predates uploaded videos.
   */
  video: VideoSummary | null;
  /** When the person marked it done, or first passed its quiz; null while it is not. */
  completedAt: string | null;
  /** Its quiz, as this person stands on it; null when it has none, or Gatekeeper predates quizzes. */
  quiz: QuizSummary | null;
  /** Present for a Company handbook process step. `materialId` is the stable shared-step key. */
  processId?: string;
  versionId?: string;
  version?: number;
  previousVersion?: number | null;
  needsReread?: boolean;
  readerUrl?: string;
  /**
   * Every one of the person's tracks holding it has passed its end date: it can
   * still be opened, but marking it done, reporting a video, the quiz and the
   * process acknowledgement are refused (`track_expired`). False from a
   * Gatekeeper that predates end dates.
   */
  locked: boolean;
}

export interface ProcessChangeSegment { type: "equal" | "insert" | "delete"; text: string }

export interface TrainingProcessResponse {
  processId: string;
  versionId: string;
  version: number;
  title: string;
  bodyHtml: string | null;
  completedAt: string | null;
  previousVersion: number | null;
  previousBodyHtml: string | null;
  changes: ProcessChangeSegment[] | null;
  changeSummaries: { version: number; summary: string | null }[];
  links: { id: string; title: string; href: string }[];
}

/**
 * `GET /api/handbook`: the Company handbook documents this person may read, and
 * only the folders on the way to them (carbon-gatekeeper 1.15.0, #235).
 */
export interface HandbookLibraryResponse {
  folders: { id: string; name: string; parentId: string | null }[];
  documents: {
    id: string;
    title: string;
    folderId: string | null;
    version: number;
    publishedAt: string;
    /** The newest version they read or acknowledged; null when never opened. */
    lastReadVersion: number | null;
    /** They read an older version, and this one is newer. */
    updatedSinceRead: boolean;
  }[];
  /** Gatekeeper answers questions (`searchAvailable`, 1.21.0, carbon-gatekeeper#246). False from an older one. */
  askAvailable: boolean;
}

/** The longest question Gatekeeper accepts, counted after trimming. Checked on both sides. */
export const MAX_QUESTION_CHARS = 500;

/** Body of POST /api/handbook/ask. Each question stands alone: nothing is kept between them. */
export interface HandbookAskRequest {
  question: string;
}

/** One handbook passage, from the current version of a document the person may open. Text only, never HTML. */
export interface HandbookPassage {
  documentId: string;
  title: string;
  version: number;
  heading: string | null;
  text: string;
}

/**
 * 200 body of POST /api/handbook/ask: Gatekeeper's answer (carbon-gatekeeper#246),
 * checked and passed on as it is. Every citation names a source's `number`.
 * `answered` always has an answer and its sources; the other two never have
 * either, only `related` passages, which are not citations.
 */
export interface HandbookAskResponse {
  outcome: "answered" | "not_covered" | "answer_unavailable";
  answer: { sentences: { text: string; citations: number[] }[]; conflict: boolean } | null;
  /** The cited passages, in citation-number order. */
  sources: (HandbookPassage & { number: number })[];
  related: HandbookPassage[];
}

/**
 * `GET /api/handbook/documents/:id[?since=N]`: the training reader's answer
 * with only what the Handbook page renders (never the earlier version's whole
 * body, the training acknowledgment or the link list: the body carries its
 * links), plus the earlier versions they read or acknowledged, which `since`
 * accepts.
 */
export interface HandbookDocumentResponse
  extends Omit<TrainingProcessResponse, "previousBodyHtml" | "completedAt" | "links"> {
  seenVersions: number[];
}

/** How far one person is through an uploaded video. Gatekeeper marks it done at 90% watched, unless it has a quiz. */
export interface VideoSummary {
  durationSeconds: number;
  /** 0 to 100, rounded down by Gatekeeper. */
  watchedPercent: number;
  lastPositionSeconds: number;
}

/** A material's quiz. Passing it is the only way such a material is marked done. */
export interface QuizSummary {
  questionCount: number;
  /** The pass mark, 1 to 100. */
  passPercent: number;
  /** Submitted attempts. */
  attempts: number;
  bestPercent: number | null;
  /** The open attempt's progress; null when none is open. */
  inProgress: { answered: number; total: number } | null;
}

/**
 * Where a track stands against its end date: `none` (no end date), `open`,
 * `due_soon` (3 days or fewer), `expired`, or `done` (finished, whatever the date).
 */
export type TrackStatus = "none" | "open" | "due_soon" | "expired" | "done";

/**
 * How a track reached the person, as Gatekeeper groups it: their department,
 * their job title, or assigned to them by hand. Gatekeeper sends the tracks in
 * that order, each group as its admin ordered it.
 */
export type TrackAssignedVia = "department" | "title" | "manual";

export interface TrainingTrack {
  id: string;
  name: string;
  description: string | null;
  /** Null from a Gatekeeper older than 1.18.0, or for a way this build does not know. */
  assignedVia: TrackAssignedVia | null;
  /** The last instant work counts (end of the day, New York); null for no end date. */
  endsAt: string | null;
  /** Gatekeeper's, as of the read; the page recounts done from the items. */
  status: TrackStatus;
  /** Past `endsAt`: its items open, but nothing done on them counts. */
  locked: boolean;
  /** In Gatekeeper's order. Progress is counted from these, so it cannot disagree with them. */
  items: TrainingItem[];
}

/** 200 body of GET /api/training; [] means nothing is assigned, including to somebody Gatekeeper does not know. */
export interface TrainingResponse {
  tracks: TrainingTrack[];
  /**
   * An opaque, stable key for the signed-in person (a hash of their Clerk user
   * id), so the browser can keep per-person state, such as unsent video
   * progress, apart on a shared machine. Never an email, a token or the id itself.
   */
  viewerKey: string;
}

/** 200 body of PUT and DELETE /api/training/completions/:materialId: what is now stored. */
export interface CompletionResponse {
  materialId: string;
  completedAt: string | null;
}

/** One question, in the attempt's own order. The right answer is never sent. */
export interface QuizQuestion {
  id: string;
  prompt: string;
  /** Opaque ids that only mean something on this attempt. */
  choices: { id: string; text: string }[];
  /** The choice id saved for this question, or null. */
  answer: string | null;
}

/** 200 body of POST /api/training/quizzes/:materialId/attempt: the open attempt, started or resumed. */
export interface QuizAttempt {
  /** Changes when the attempt is submitted elsewhere and a new one starts. */
  attemptId: string;
  materialId: string;
  passPercent: number;
  total: number;
  answered: number;
  /** The first unanswered question; null when all are answered. */
  resumeAt: string | null;
  questions: QuizQuestion[];
}

/** A graded attempt: which questions were missed, never their right answers. */
export interface QuizResult {
  correct: number;
  total: number;
  percent: number;
  passed: boolean;
  passPercent: number;
  missed: { questionId: string; prompt: string }[];
}

/** 200 body of GET /api/training/quizzes/:materialId/attempt; either may be null. */
export interface QuizStateResponse {
  attempt: QuizAttempt | null;
  lastResult: QuizResult | null;
}

/** Body of PUT /api/training/quizzes/:materialId/attempt/answers/:questionId. */
export interface QuizAnswerRequest {
  choiceId: string;
}

/** 200 body of that PUT. */
export interface QuizAnswerResponse {
  attemptId: string;
  answered: number;
  total: number;
  resumeAt: string | null;
}

/** 200 body of POST /api/training/quizzes/:materialId/attempt/submit. `completedAt` is set by a pass, now or earlier. */
export interface QuizSubmitResponse {
  result: QuizResult;
  completedAt: string | null;
}

/**
 * 200 body of GET /api/training/materials/:materialId/playback. `url` and
 * `posterUrl` are presigned bucket URLs, a bearer credential until `expiresAt`:
 * the browser plays them directly, and carbon-os never logs or stores them.
 */
export interface VideoPlayback {
  materialId: string;
  url: string;
  /** ISO, on Gatekeeper's clock. Ask again before it passes, or on a media error. */
  expiresAt: string;
  /**
   * Seconds left until `expiresAt`, from carbon-os's clock when it answered,
   * never below 0. The browser schedules from this and its own monotonic
   * clock, so a browser whose wall clock is wrong still plays to the end.
   */
  expiresInSeconds: number;
  posterUrl: string | null;
  durationSeconds: number;
  resumeAtSeconds: number;
  watchedPercent: number;
}

/** Gatekeeper's MAX_RANGES_PER_REPORT: the most ranges one progress report may carry. */
/** Binds a browser report to the opaque viewer that watched it, checked before forwarding. */
export const VIDEO_VIEWER_HEADER = "X-CarbonOS-Viewer";

export const MAX_RANGES_PER_REPORT = 100;

export type VideoEndpoint = "playback" | "progress";

/**
 * An uploaded video's playback or progress path: carbon-os's for the browser,
 * or the same under `/api/me` for Gatekeeper's.
 */
export function videoPath(materialId: string, what: VideoEndpoint, api: "carbon-os" | "gatekeeper" = "carbon-os"): string {
  return `${api === "gatekeeper" ? "/api/me" : "/api"}/training/materials/${encodeURIComponent(materialId)}/${what}`;
}

/** A watched stretch, `[start, end]` in seconds. */
export type WatchedRange = [number, number];

/** A finite, non-negative number of seconds. */
export function isSeconds(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

/** `[start, end]`, both seconds, start before end: the one rule for a range, wherever one is read. */
export function isWatchedRange(value: unknown): value is WatchedRange {
  return Array.isArray(value) && value.length === 2 && isSeconds(value[0]) && isSeconds(value[1]) && value[0] < value[1];
}

/** Body of PUT /api/training/materials/:materialId/progress. */
export interface VideoProgressRequest {
  /** At most 100; each finite, 0 or more, start before end. */
  ranges: WatchedRange[];
  positionSeconds: number;
}

/** 200 body of that PUT: what Gatekeeper now stores. `completedAt` is the material's completion, whatever made it. */
export interface VideoProgress {
  materialId: string;
  watchedSeconds: number;
  watchedPercent: number;
  lastPositionSeconds: number;
  completedAt: string | null;
}

export type TrainingErrorCode =
  /** 401: no carbon-os session, or Gatekeeper said 401. */
  | "not_signed_in"
  /** 403: the account's email is unverified. */
  | "wrong_account"
  /** 404: the material id is not one, or Gatekeeper has no such material (or, for playback and progress, no uploaded video). */
  | "unknown_material"
  | "unknown_process"
  /** 404: `since` names a version this person never read or acknowledged, or a read names one that is not current. */
  | "unknown_version"
  /** 404: this Gatekeeper predates the handbook library (before 1.15.0); the page says it is not available yet. */
  | "handbook_unsupported"
  /** 404: this Gatekeeper predates handbook questions (before 1.21.0); the page hides the ask box. */
  | "search_unsupported"
  /** 400: a question that is empty or longer than MAX_QUESTION_CHARS. Never forwarded. */
  | "invalid_query"
  /** 429: the person's hourly allowance of questions is used up; see `retryAfterMinutes`. */
  | "rate_limited"
  /** 503/504: Gatekeeper could not search, did not answer in time, or answered something malformed. */
  | "search_unavailable"
  | "version_changed"
  | "bad_body"
  /** 404: the material has no published quiz, or its id is not one. */
  | "unknown_quiz"
  /** 400: a question or choice that is not on the attempt, or no usable body. */
  | "invalid_answer"
  /** 409: no attempt is open to answer or submit. */
  | "no_attempt"
  /** 409: submitted with a question unanswered; nothing was graded. */
  | "incomplete"
  /** 409: a tick on a material with a quiz, which only passing it marks. */
  | "quiz_required"
  /** 409: every track holding the item has passed its end date; nothing was saved. */
  | "track_expired"
  /** 400: a progress body that is not ranges and a position, or too big. */
  | "invalid_progress"
  /** 409: the session changed since these ranges were watched; nothing was forwarded. */
  | "viewer_changed"
  /** 422: more watched than the time since the last report allows; nothing was stored. */
  | "implausible_progress"
  /** 503: Gatekeeper's bucket is not configured or not answering. */
  | "storage_unavailable"
  /** 403: verified, but Gatekeeper does not know this person. Nobody gets in without it. */
  | "not_in_gatekeeper"
  /** 503: GATEKEEPER_URL unset, or Clerk unconfigured (development only). */
  | "not_connected"
  /** 502/503/504: timeout, 5xx, 503, network failure, bad body or unexpected status. */
  | "gatekeeper_unavailable";

/** Every /api/training answer other than a 200, and a file download that did not start. */
export interface TrainingError {
  /** A sentence for a person. */
  error: string;
  code: TrainingErrorCode;
  /** "/auth/session" on 401. */
  signInUrl?: string;
  /** "/auth/signout" on 403. */
  signOutUrl?: string;
  /** On 403, so the page can say which account was refused. */
  signedInAs?: string;
  /** On a handbook question's 429 `rate_limited`, rounded up; absent when Gatekeeper did not say. */
  retryAfterMinutes?: number;
}
